import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Menu, X, Linkedin, ArrowLeft } from 'lucide-react';
import { WatercolorBlob } from './watercolor';
import { projects, featuredProjects } from './components/ProjectData.js';
import { artGallery } from './components/PillarData.js';
import './components/Styles.css';
import emmaImg from './images/Emma-img.jpeg';
import emLogo from './images/Logo.svg';
import squiggleImg from './images/squiggle.png';

// ─── Scroll to top on route change ───────────────────────────────────────────
const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
};

// ─── Squiggle arrow ───────────────────────────────────────────────────────────
const Squiggle = () => (
    <img src={squiggleImg} alt="" aria-hidden="true" className="squiggle" />
);

// ─── Helper: format category label ───────────────────────────────────────────
const getCategory = (project) => {
    if (project.category === 'Exhibition') return 'Installation / Exhibition';
    if (project.type === 'game')           return 'Game Design / Development';
    return 'Product / UI / UX';
};

// ─── App ──────────────────────────────────────────────────────────────────────
const App = () => (
    <>
        <ScrollToTop />
        <Routes>
            <Route path="/"                     element={<HomePage />} />
            <Route path="/about"                element={<AboutPage />} />
            <Route path="/art"                  element={<ArtPage />} />
            <Route path="/project/:projectId"    element={<ProjectDetailPage />} />
        </Routes>
    </>
);

// ─── Shared Nav ───────────────────────────────────────────────────────────────
const Nav = ({ accentColor }) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const scrollToProjects = () => {
        const el = document.getElementById('projects');
        if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 110;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    const goToProjects = (e) => {
        e.preventDefault();
        if (location.pathname === '/') {
            scrollToProjects();
        } else {
            navigate('/');
            setTimeout(scrollToProjects, 100);
        }
        setMobileOpen(false);
    };

    return (
        <nav className="nav">
            <div className="navContainer">
                {/* Logo */}
                <Link to="/" className="logoText">Emma McMillin</Link>

                {/* Hamburger (mobile) */}
                <button
                    onClick={() => setMobileOpen(!mobileOpen)}
                    className="hamburgerMenu"
                    style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem', color: '#2c3e50' }}
                >
                    {mobileOpen ? <X size={24} /> : <Menu size={24} />}
                </button>

                {/* Desktop links */}
                <ul className="navLinks">
                    <li><a href="/#projects" className="navLink" onClick={goToProjects}>Projects</a></li>
                    <li><Link to="/art" className="navLink">Art</Link></li>
                    <li><Link to="/about" className="navLink">About</Link></li>
                </ul>
            </div>

            {/* Mobile overlay */}
            {mobileOpen && (
                <div style={{ position: 'fixed', top: '64px', left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(248,249,250,0.98)', backdropFilter: 'blur(10px)', zIndex: 999, padding: '2rem' }}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {[
                            { label: 'Projects', href: '/#projects', onClick: goToProjects },
                            { label: 'Art',      href: '/art' },
                            { label: 'About',    href: '/about' },
                        ].map(({ label, href, onClick }) => (
                            <li key={label} style={{ marginBottom: '1.5rem' }}>
                                <a href={href} onClick={onClick ?? (() => setMobileOpen(false))} style={{ fontSize: '1.25rem', fontWeight: '500', color: '#2c3e50', textDecoration: 'none', display: 'block', padding: '0.75rem 0' }}>
                                    {label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </nav>
    );
};

// ─── Home Page ────────────────────────────────────────────────────────────────
const HomePage = () => (
    <>
        <WatercolorBlob />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <Nav />

            {/* ── Hero ── */}
            <section className="homeHero">
                <h1 className="homeHeroTitle">
                    Hi! I'm Emma McMillin, product &amp; interaction designer based out of Seattle, WA.
                </h1>
                <p className="homeHeroSubtitle">See a collection of my work below</p>
                <Squiggle />
            </section>

            {/* ── Projects ── */}
            <section id="projects" className="projectsListSection">
                <h2 className="projectsListHeading">Projects</h2>
                <hr className="projectsDivider" />

                {featuredProjects.map((project) => (
                    <Link
                        key={project.id}
                        to={`/project/${project.id}`}
                        className="projectRow scroll-fade"
                    >
                        <div className="projectRowThumb">
                            <img
                                src={project.coverImage ?? project.images[0]}
                                alt={project.title}
                                className="projectRowThumbImg"
                            />
                        </div>

                        <div className="projectRowInfo">
                            <span className="projectRowCategory">{getCategory(project)}</span>
                            <h3 className="projectRowTitle">{project.title}</h3>
                            {project.description && (
                                <p className="projectRowDesc">{project.description}</p>
                            )}
                            <span className="projectRowDetailsLink">Details</span>
                        </div>
                    </Link>
                ))}
            </section>

            {/* ── Footer ── */}
            <footer id="contact" className="homeFooter">
                <span className="footerHeading">Let's work together</span>
                <a href="https://www.linkedin.com/in/emma-mcmillin/" target="_blank" rel="noopener noreferrer" className="footerIcon">
                    <Linkedin size={28} strokeWidth={1.5} />
                </a>
            </footer>
        </div>
    </>
);

// ─── Project Detail Page ──────────────────────────────────────────────────────
const ProjectDetailPage = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const project = projects.find((p) => String(p.id) === String(projectId));

    if (!project) {
        return (
            <div style={{ padding: '8rem 2rem', textAlign: 'center' }}>
                <p>Project not found.</p>
                <Link to="/">← Back home</Link>
            </div>
        );
    }

    const moreProjects = featuredProjects.filter((p) => p.id !== project.id).slice(0, 3);
    const imgs         = project.images ?? [];
    const caps         = project.captions ?? [];
    const heroImg      = imgs[0] ?? null;
    const postStatsImg = imgs[1] ?? null;   // shown below stats
    // Figma: last image = full-width landscape strip, second-to-last = centered single
    const fullWidthImg = imgs.length > 1 ? imgs[imgs.length - 1] : null;
    const centeredImg  = imgs.length > 2 ? imgs[imgs.length - 2] : null;
    const heroCaption      = caps[0] ?? null;
    const postStatsCaption = caps[1] ?? null;
    const fullWidthCaption = imgs.length > 1 ? (caps[imgs.length - 1] ?? null) : null;
    const centeredCaption  = imgs.length > 2 ? (caps[imgs.length - 2] ?? null) : null;

    return (
        <>
            <div className="container projectPage">
                <Nav />

                {/* ── Page header: title + rule ── */}
                <div className="pd2Header">
                    <div className="pd2TitleRow">
                        <button className="pd2BackBtn" onClick={() => { navigate('/'); setTimeout(() => { document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' }); }, 100); }}>
                            <ArrowLeft size={20} />
                        </button>
                        <h1 className="pd2PageTitle">{project.title}</h1>
                    </div>
                    <hr className="pd2Rule" />
                </div>

                {/* ── Hero: subtitle text left, image right ── */}
                <div className="pd2Hero">
                    <p className="pd2HeroText">
                        {project.subtitle || project.description}
                    </p>
                    {heroImg && (
                        <div className="pd2HeroImgBox">
                            <img src={heroImg} alt={project.title} className="pd2HeroImg" />
                            {heroCaption && <p className="pd2Caption">{heroCaption}</p>}
                        </div>
                    )}
                </div>

                {project.blocks ? (
                    /* ── Blocks mode: full custom layout ── */
                    project.blocks.map((block, i) => {
                        if (block.type === 'text') return (
                            <div key={i} className="pd2Block pd2Block--text scroll-fade">
                                {block.heading && <h3 className="pd2BlockHeading">{block.heading}</h3>}
                                {block.body && <p className="pd2BlockBody">{block.body}</p>}
                            </div>
                        );
                        if (block.type === 'image') return (
                            <div key={i} className={`pd2Block pd2Block--image pd2Image--${block.size || 'full'} scroll-fade`}>
                                <img src={block.src} alt={block.caption || ''} />
                                {block.caption && <p className="pd2Caption">{block.caption}</p>}
                            </div>
                        );
                        if (block.type === 'video') return (
                            <div key={i} className={`pd2Block pd2Video pd2Video--${block.size || 'full'} scroll-fade`}>
                                <video autoPlay muted loop playsInline>
                                    <source src={block.src} type="video/mp4" />
                                </video>
                                {block.caption && <p className="pd2Caption">{block.caption}</p>}
                            </div>
                        );
                        if (block.type === 'columns') return (
                            <div key={i} className="pd2Block pd2Block--columns scroll-fade">
                                {block.items.map((col, j) => (
                                    <div key={j} className="pd2BlockCol">
                                        {col.src && <img src={col.src} alt={col.caption || col.heading || ''} className="pd2BlockColImg" />}
                                        {col.heading && <h3 className="pd2BlockHeading">{col.heading}</h3>}
                                        {col.body && <p className="pd2BlockBody">{col.body}</p>}
                                        {col.caption && <p className="pd2Caption">{col.caption}</p>}
                                    </div>
                                ))}
                            </div>
                        );
                        if (block.type === 'metrics') return (
                            <div key={i} className="pd2Metrics scroll-fade">
                                {block.items.map((item, j) => (
                                    <div key={j} className="pd2MetricItem">
                                        <span className="pd2MetricLabel">
                                            {item.metric ? `${item.metric} ${item.label}` : item.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        );
                        return null;
                    })
                ) : (
                    /* ── Legacy mode: fixed slot layout ── */
                    <>
                        {/* Two-column text sections */}
                        {project.sections?.length > 0 && (
                            <div className="pd2Sections scroll-fade">
                                {project.sections.slice(0, 2).map((s) => (
                                    <div key={s.heading} className="pd2SectionCol">
                                        <h3 className="pd2SectionHeading">{s.heading}</h3>
                                        <p className="pd2SectionBody">{s.body}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Video */}
                        {project.video && (
                            <div className="pd2Video scroll-fade">
                                <video autoPlay muted loop playsInline>
                                    <source src={project.video} type="video/mp4" />
                                </video>
                                {project.videoCaption && <p className="pd2Caption">{project.videoCaption}</p>}
                            </div>
                        )}

                        {/* Full-width landscape strip */}
                        {fullWidthImg && (
                            <div className="pd2FullWidthImg scroll-fade">
                                <img src={fullWidthImg} alt="" />
                                {fullWidthCaption && <p className="pd2Caption">{fullWidthCaption}</p>}
                            </div>
                        )}

                        {/* Centered single image */}
                        {centeredImg && (
                            <div className="pd2FeaturedImg scroll-fade">
                                <img src={centeredImg} alt="" />
                                {centeredCaption && <p className="pd2Caption">{centeredCaption}</p>}
                            </div>
                        )}

                        {/* Stats / metrics */}
                        {project.businessImpact?.length > 0 && (
                            <div className="pd2Metrics scroll-fade">
                                {project.businessImpact.map((item, i) => (
                                    <div key={i} className="pd2MetricItem">
                                        <span className="pd2MetricLabel">
                                            {item.metric ? `${item.metric} ${item.label}` : item.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Post-stats image */}
                        {postStatsImg && project.businessImpact?.length > 0 && (
                            <div className="pd2FeaturedImg pd2FeaturedImg--gif scroll-fade">
                                <img src={postStatsImg} alt="" />
                                {postStatsCaption && <p className="pd2Caption">{postStatsCaption}</p>}
                            </div>
                        )}
                    </>
                )}

                {/* ── More Projects ── */}
                <div className="pd2More scroll-fade">
                    <hr className="pd2Rule" />
                    <h2 className="pd2MoreHeading">More Projects</h2>
                    <div className="pd2MoreGrid">
                        {moreProjects.map((p) => (
                            <Link key={p.id} to={`/project/${p.id}`} className="pd2MoreCard">
                                <div className="pd2MoreCardImg">
                                    <img src={p.coverImage ?? p.images[0]} alt={p.title} />
                                </div>
                                <span className="pd2MoreCardTitle">{p.title}</span>
                            </Link>
                        ))}
                    </div>
                </div>

                {/* ── Footer ── */}
                <footer id="contact" className="homeFooter">
                    <span className="footerHeading">Let's work together</span>
                    <a href="https://www.linkedin.com/in/emma-mcmillin/" target="_blank" rel="noopener noreferrer" className="footerIcon">
                        <Linkedin size={28} strokeWidth={1.5} />
                    </a>
                </footer>
            </div>
        </>
    );
};

// ─── Art Page ─────────────────────────────────────────────────────────────────
const ArtPage = () => {
    const [lightbox, setLightbox] = useState(null);

    const block = (e) => e.preventDefault();

    return (
        <>
            <div className="container projectPage">
                <Nav />

                {/* ── Page header ── */}
                <div className="pd2Header">
                    <h1 className="pd2PageTitle">Art</h1>
                    <hr className="pd2Rule" />
                </div>

                {/* ── Illustration gallery ── */}
                <section className="artSection scroll-fade">
                    <div className="artGalleryGrid">
                        {artGallery.map((item, i) => (
                            <div key={i} className="artGalleryItem" onClick={() => setLightbox(item)}>
                                <div className="artGalleryImgWrap">
                                    <img src={item.src} alt={item.caption} className="artGalleryImg"
                                        onContextMenu={block} onDragStart={block} />
                                    <div className="artGalleryImgShield" />
                                </div>
                                {item.caption && (
                                    <p className="artGalleryCaption">{item.caption}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                {/* ── Lightbox ── */}
                {lightbox && (
                    <div className="lightboxOverlay" onClick={() => setLightbox(null)}>
                        <div className="lightboxContent" onClick={(e) => e.stopPropagation()}>
                            <button className="lightboxClose" onClick={() => setLightbox(null)}>✕</button>
                            <div className="lightboxImgWrap">
                                <img src={lightbox.src} alt={lightbox.caption} className="lightboxImg"
                                    onContextMenu={block} onDragStart={block} />
                                <div className="lightboxImgShield" />
                            </div>
                            {lightbox.caption && <p className="lightboxCaption">{lightbox.caption}</p>}
                        </div>
                    </div>
                )}

                {/* ── Footer ── */}
                <footer id="contact" className="homeFooter">
                    <span className="footerHeading">Let's work together</span>
                    <a href="https://www.linkedin.com/in/emma-mcmillin/" target="_blank" rel="noopener noreferrer" className="footerIcon">
                        <Linkedin size={28} strokeWidth={1.5} />
                    </a>
                </footer>
            </div>
        </>
    );
};

// ─── About Page ───────────────────────────────────────────────────────────────
const tools = 'Photoshop  •  Illustrator  •  Blender 3D  •  Figma  •  Unity  •  Premiere Pro  •  C#  •  HTML/CSS  •  ';

const AboutPage = () => (
    <>
        <div className="container projectPage">
            <Nav />

            {/* ── Page header ── */}
            <div className="pd2Header">
                <h1 className="pd2PageTitle">About Me</h1>
                <hr className="pd2Rule" />
            </div>

            {/* ── Bio: text left, portrait right ── */}
            <div className="aboutContent">
                <div className="aboutText">
                    <h2 className="aboutGreeting">Hi there!</h2>
                    <p className="aboutBody">
                        I'm Emma McMillin (she/her), a senior product designer based in Seattle, WA.
                        I specialize in end-to-end design, from concept through launch, making complex
                        technical products feel simple and clear, whether I'm designing core product
                        surfaces, building prototypes in code, or shaping design systems.
                    </p>
                    <p className="aboutBody">
                        I have a BA and MA in Interaction &amp; Game Design from Zürich University of
                        the Arts and a foundation in illustration and design from Pacific Northwest
                        College of Art.
                    </p>
                </div>
                <div className="aboutImageWrap">
                    <img src={emmaImg} alt="Emma McMillin" className="aboutImage" />
                </div>
            </div>

            {/* ── Scrolling tools marquee ── */}
            <div className="toolsMarquee">
                <div className="toolsTrack">
                    <span>{tools}</span>
                    <span>{tools}</span>
                </div>
            </div>

            {/* ── Footer ── */}
            <footer id="contact" className="homeFooter">
                <span className="footerHeading">Let's get in touch!</span>
                <a href="https://www.linkedin.com/in/emma-mcmillin/" target="_blank" rel="noopener noreferrer" className="footerIcon">
                    <Linkedin size={28} strokeWidth={1.5} />
                </a>
            </footer>
        </div>
    </>
);

export default App;
